import { Suspense, lazy, useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { TabBar } from '@/components/TabBar';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { FeedSkeleton, PageSkeleton } from '@/components/Skeleton';
import { useVibe } from '@/lib/vibe';

const ForYou = lazy(() => import('@/pages/ForYou').then((m) => ({ default: m.ForYou })));
const Matches = lazy(() => import('@/pages/Matches').then((m) => ({ default: m.Matches })));
const MatchDetail = lazy(() =>
  import('@/pages/MatchDetail').then((m) => ({ default: m.MatchDetail })),
);
const TeamProfile = lazy(() =>
  import('@/pages/TeamProfile').then((m) => ({ default: m.TeamProfile })),
);
const PlayerProfile = lazy(() =>
  import('@/pages/PlayerProfile').then((m) => ({ default: m.PlayerProfile })),
);
const LeagueTable = lazy(() =>
  import('@/pages/LeagueTable').then((m) => ({ default: m.LeagueTable })),
);
const Me = lazy(() => import('@/pages/Me').then((m) => ({ default: m.Me })));

// Dev-only design-system reference pages. Still routed in prod — they don't
// hurt, and the design team uses them for regressions.
const Foundations = lazy(() =>
  import('@/pages/design-system/Foundations').then((m) => ({ default: m.Foundations })),
);
const Particles = lazy(() =>
  import('@/pages/design-system/Particles').then((m) => ({ default: m.Particles })),
);
const Atoms = lazy(() =>
  import('@/pages/design-system/Atoms').then((m) => ({ default: m.Atoms })),
);

function FeedLoading() {
  return <FeedSkeleton />;
}
function PageLoading() {
  return <PageSkeleton />;
}

export function App() {
  const vibe = useVibe();
  // Mirror vibe onto :root so token overrides cascade into html > body,
  // not just into .app-shell's subtree. Without this the body background
  // would keep the default --bg-0 while only cards inside .app-shell
  // picked up the new theme.
  useEffect(() => {
    const prev = document.documentElement.getAttribute('data-vibe');
    document.documentElement.setAttribute('data-vibe', vibe);
    return () => {
      if (prev) document.documentElement.setAttribute('data-vibe', prev);
      else document.documentElement.removeAttribute('data-vibe');
    };
  }, [vibe]);

  return (
    <div className="app-shell" data-vibe={vibe}>
      <div className="app-body">
        <ErrorBoundary>
          <Routes>
            <Route path="/" element={<Navigate to="/matches" replace />} />
            <Route
              path="/for-you"
              element={
                <Suspense fallback={<FeedLoading />}>
                  <ForYou />
                </Suspense>
              }
            />
            <Route
              path="/matches"
              element={
                <Suspense fallback={<PageLoading />}>
                  <Matches />
                </Suspense>
              }
            />
            <Route
              path="/match/:id"
              element={
                <Suspense fallback={<PageLoading />}>
                  <MatchDetail />
                </Suspense>
              }
            />
            <Route
              path="/team/:id"
              element={
                <Suspense fallback={<PageLoading />}>
                  <TeamProfile />
                </Suspense>
              }
            />
            <Route
              path="/player/:id"
              element={
                <Suspense fallback={<PageLoading />}>
                  <PlayerProfile />
                </Suspense>
              }
            />
            <Route
              path="/table/:leagueId"
              element={
                <Suspense fallback={<PageLoading />}>
                  <LeagueTable />
                </Suspense>
              }
            />
            <Route
              path="/me"
              element={
                <Suspense fallback={<PageLoading />}>
                  <Me />
                </Suspense>
              }
            />

            <Route
              path="/design-system/foundations"
              element={
                <Suspense fallback={<PageLoading />}>
                  <Foundations />
                </Suspense>
              }
            />
            <Route
              path="/design-system/particles"
              element={
                <Suspense fallback={<PageLoading />}>
                  <Particles />
                </Suspense>
              }
            />
            <Route
              path="/design-system/atoms"
              element={
                <Suspense fallback={<PageLoading />}>
                  <Atoms />
                </Suspense>
              }
            />

            <Route path="*" element={<Navigate to="/matches" replace />} />
          </Routes>
        </ErrorBoundary>
      </div>
      <TabBar />
    </div>
  );
}
